// src/hooks/offline.js
// React hooks for the offline persistence layer.
// Never calls fetch or IDB directly — that all goes through services/sync.

import { useState, useEffect, useCallback, useRef } from 'react';
import { SyncQueue }         from '@services/storage/idb';
import { StaleCacheManager } from '@services/sync/staleCache.js';
import { onSyncEvent }       from '@services/sync/engine.js';
import { applyUpdate }       from '@services/storage/sw';
import { REFRESH }           from '@config';

// ── useSyncQueue ──────────────────────────────────────────────────
// Live queue counts for status badges and offline indicators.
export function useSyncQueue() {
  const [counts,    setCounts]    = useState({ pending: 0, syncing: 0, done: 0, dead: 0 });
  const [lastEvent, setLastEvent] = useState(null);

  const refresh = useCallback(async () => {
    setCounts(await SyncQueue.countByStatus());
  }, []);

  useEffect(() => {
    refresh();
    const unsub = onSyncEvent((ev) => { setLastEvent(ev); refresh(); });
    window.addEventListener('na-queue-changed', refresh);
    window.addEventListener('na-sync-complete', refresh);
    return () => {
      unsub();
      window.removeEventListener('na-queue-changed', refresh);
      window.removeEventListener('na-sync-complete', refresh);
    };
  }, [refresh]);

  return { counts, lastEvent, pendingCount: counts.pending, deadCount: counts.dead };
}

// ── useDeadLetterQueue ────────────────────────────────────────────
// Items that exhausted all retries — SOS failures surface here.
export function useDeadLetterQueue() {
  const [dead, setDead] = useState([]);

  const refresh = useCallback(async () => {
    const all = await SyncQueue.getAll();
    setDead(all.filter((i) => i.status === 'dead'));
  }, []);

  useEffect(() => {
    refresh();
    const unsub = onSyncEvent((ev) => { if (ev.type === 'dead') refresh(); });
    window.addEventListener('na-sos-failed', refresh);
    return () => {
      unsub();
      window.removeEventListener('na-sos-failed', refresh);
    };
  }, [refresh]);

    const retryItem = useCallback(async (syncId) => {
      // Reset item to pending so sync engine picks it up
      const all  = await SyncQueue.getAll();
      const item = all.find(i => i.syncId === syncId);
      if (!item) return;
      console.log('[Retry] Item status:', item.status, 'attempts:', item.attempts, 'type:', item.type);
      // SyncQueue has no update method — use txPut to reset item to pending
      const { txPut } = await import('@services/storage/idb');
      await txPut('sync_queue', { ...item, status: 'pending', attempts: 0,
                                   nextRetryAt: Date.now(), lastError: null,
                                   updatedAt: Date.now() });
      console.log('[Retry] Reset to pending, dispatching online event');
      const { useOfflineStore: _ofs } = await import('@store/offlineStore');
      _ofs.getState().clearDeadItem(syncId);
      // Small delay to ensure IDB write commits before engine reads
      await new Promise(r => setTimeout(r, 300));
      // Call drainQueue directly rather than relying on event dispatch
      try {
        const { drainQueue } = await import('@services/sync/engine');
        await drainQueue();
      } catch {
        window.dispatchEvent(new Event('online'));
      }
      refresh();
    }, [refresh]);

    const retryAll = useCallback(async (syncIds) => {
      // Reset ALL items to pending first, then drain once
      const all = await SyncQueue.getAll();
      const { txPut } = await import('@services/storage/idb');
      await Promise.all(syncIds.map(async (syncId) => {
        const item = all.find(i => i.syncId === syncId);
        if (!item) return;
        console.log('[RetryAll] Resetting:', syncId, 'status:', item.status);
        try {
          await txPut('sync_queue', { ...item, status: 'pending', attempts: 0,
                                       nextRetryAt: Date.now(), lastError: null,
                                       updatedAt: Date.now() });
          console.log('[RetryAll] IDB write success for:', syncId);
        } catch(e) {
          console.error('[RetryAll] IDB write FAILED:', syncId, e.message);
        }
        const { useOfflineStore: _ofs } = await import('@store/offlineStore');
        _ofs.getState().clearDeadItem(syncId);
      }));
      // Single drain after all resets complete
      await new Promise(r => setTimeout(r, 300));
      try {
        const { drainQueue } = await import('@services/sync/engine');
        console.log('[RetryAll] Draining queue');
        await drainQueue();
      } catch {
        window.dispatchEvent(new Event('online'));
      }
      refresh();
    }, [refresh]);

    const dismissItem = useCallback(async (syncId) => {
      await SyncQueue.markDone(syncId).catch(() => {});
      const { useOfflineStore: _ofs2 } = await import('@store/offlineStore');
      _ofs2.getState().clearDeadItem(syncId);
      _ofs2.getState().clearSosFailure?.();
      setDead(d => d.filter(i => i.syncId !== syncId));
    }, []);

  return {
    dead,
    hasDead:      dead.length > 0,
    hasSosFailed: dead.some((i) => i.type === 'create_sos'),
    retryItem,
    retryAll,
    dismissItem,
  };
}

// ── useStaleWhileRevalidate ───────────────────────────────────────
// Return type: { data, loading, error, lastUpdated, isStale, reload }
//
// Behaviour:
//   1. Immediately serve from IDB cache (even stale) → no loading flash
//   2. If stale, revalidate in background via StaleCacheManager
//   3. If forceNetwork or nothing in cache, call fetchFn directly
//   4. Keep stale data visible on network error (never blank the screen)
//   5. Auto-revalidate on reconnect and on app foreground
//
// cacheKey: one of 'nws_alerts' | 'usgs_gauges' | '511la_incidents'
//           pass null to skip IDB cache and always go to network
export function useStaleWhileRevalidate(
  fetchFn,
  { cacheKey = null, intervalMs = REFRESH.alerts, enabled = true } = {},
) {
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isStale,     setIsStale]     = useState(false);
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  const load = useCallback(async ({ background = false, forceNetwork = false } = {}) => {
    if (!enabled) return;
    if (!background) setLoading(true);
    setError(null);

    // ── Step 1: serve from IDB cache immediately ──────────────────
    if (cacheKey && !forceNetwork) {
      try {
        const cached = await StaleCacheManager.get(cacheKey);
        if (cached?.data) {
          setData(cached.data);
          setIsStale(!!cached.stale);
          setLastUpdated(cached.cachedAt ?? Date.now());
          setLoading(false);
          if (!cached.stale) return;  // still fresh — skip network
        }
      } catch {
        // IDB unavailable — fall through to network
      }
    }

    // ── Step 2: fetch fresh from network ─────────────────────────
    try {
      const fresh = await fetchRef.current();
      setData(fresh);
      setIsStale(false);
      setLastUpdated(Date.now());
    } catch (err) {
      // Classify the failure so the UI can show the right message
      const isOffline = !navigator.onLine;
      setError(
        isOffline ? 'offline'
        : err.message?.includes('timeout') ? 'timeout'
        : err.message ?? 'fetch_failed'
      );
      // Keep whatever data is already displayed — don't wipe it
    } finally {
      setLoading(false);
    }
  }, [cacheKey, enabled]);

  // Mount + periodic refresh
  useEffect(() => {
    load();
    if (!intervalMs) return;
    const id = setInterval(() => load({ background: true }), intervalMs);
    return () => clearInterval(id);
  }, [load, intervalMs]);

  // Revalidate on reconnect
  useEffect(() => {
    const handler = () => load({ background: true, forceNetwork: true });
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [load]);

  // Revalidate when tab regains focus
  useEffect(() => {
    const handler = () => {
      if (!document.hidden) load({ background: true });
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [load]);

  // Revalidate when StaleCacheManager signals fresh data for our key
  useEffect(() => {
    const handler = (ev) => {
      if (!cacheKey || ev.detail?.key === cacheKey) load({ background: true });
    };
    window.addEventListener('na-cache-updated', handler);
    return () => window.removeEventListener('na-cache-updated', handler);
  }, [load, cacheKey]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    isStale,
    reload: () => load({ forceNetwork: true }),
  };
}

// ── useConnectivity ───────────────────────────────────────────────
export function useConnectivity() {
  const [online, setOnline] = useState(
    () => typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  useEffect(() => {
    const on  = () => {
      setOnline(true);
      import('@store/offlineStore').then(({ useOfflineStore }) => {
        useOfflineStore.getState().setOnline(true);
      });
    };
    const off = () => {
      setOnline(false);
      import('@store/offlineStore').then(({ useOfflineStore }) => {
        useOfflineStore.getState().setOnline(false);
      });
    };
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);

    let cancelled = false;
    let failCount = 0;

    function markOffline() {
      if (cancelled) return;
      setOnline(false);
      import('@store/offlineStore').then(({ useOfflineStore }) => {
        useOfflineStore.getState().setOnline(false);
      });
    }

    function markOnline() {
      if (cancelled) return;
      failCount = 0;
      setOnline(true);
      import('@store/offlineStore').then(({ useOfflineStore }) => {
        useOfflineStore.getState().setOnline(true);
      });
    }

    function xhrProbe(url) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('HEAD', url + '?nc=' + Date.now(), true);
        xhr.timeout = 5000;
        xhr.onload  = () => { console.log('[connectivity] XHR ok:', url, xhr.status); resolve(true); };
        xhr.onerror = () => { console.log('[connectivity] XHR error:', url); reject(new Error('xhr failed')); };
        xhr.ontimeout = () => { console.log('[connectivity] XHR timeout:', url); reject(new Error('xhr timeout')); };
        xhr.onabort = () => { console.log('[connectivity] XHR abort:', url); reject(new Error('xhr abort')); };
        try { xhr.send(); } catch(e) { console.log('[connectivity] XHR throw:', url, e.message); reject(e); }
      });
    }

    async function probe() {
      // probe start
      const endpoints = [
        window.location.origin + '/favicon.ico',
        window.location.origin + '/manifest.json',
      ];

      let anySuccess = false;
      for (const url of endpoints) {
        try {
          await xhrProbe(url);
          anySuccess = true;
          break;
        } catch {
          // try next
        }
      }

      if (anySuccess) {
        console.log('[connectivity] ONLINE');
        markOnline();
      } else {
        failCount++;

        if (failCount >= 2) markOffline();
      }
    }

    // Also watch Firestore connection state as offline signal
    (async () => {
      try {
        const { initFirebase } = await import('@services/storage/firebase');
        const { db } = await initFirebase();
        if (!db) return;
        const { enableNetwork, disableNetwork } = await import('firebase/firestore');
        // Firestore has its own offline detection — mirror it
        const { collection, onSnapshot } = await import('firebase/firestore');
        // Sentinel doc probe — if this fails, we're offline
      } catch { /* ignore */ }
    })();

    const intervalId = setInterval(probe, 60_000);
    probe();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('online',  on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

// ── useSWUpdate ───────────────────────────────────────────────────
export function useSWUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    const handler = () => {
      setUpdateAvailable(true);
      // Also update the offline store so any component can react
      import('@store/offlineStore').then(({ useOfflineStore }) => {
        useOfflineStore.getState().setSwUpdateAvailable(true);
      });
    };
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);
  return { updateAvailable, applyUpdate };
}

// ── useMapTileCache ───────────────────────────────────────────────
export function useMapTileCache() {
  const [cachedCount, setCachedCount] = useState(0);
  const cacheTiles = useCallback((urls) => {
    navigator.serviceWorker?.controller?.postMessage({ type: 'PREFETCH_TILES', urls });
  }, []);
  useEffect(() => {
    const handler = (ev) => setCachedCount((n) => n + (ev.detail?.count ?? 0));
    window.addEventListener('na-tiles-cached', handler);
    return () => window.removeEventListener('na-tiles-cached', handler);
  }, []);
  return { cacheTiles, cachedCount };
}
